import React, { useState, useEffect } from "react";
import GlobalStyles, { CommonClasses } from "../../global/GlobalStyle";
import { useAlert } from "../../contexts/AlertContext";
import { adminAPI } from "../../services/api";
import { User } from "../../types";
import { MdAccountBalance, MdSearch, MdAdd, MdRefresh } from "react-icons/md";

// Define the balance keys type
type BalanceKey = "ledger" | "stake" | "bonus" | "earnings";
type BalanceField = `${BalanceKey}_balance`;

interface BalanceType {
  key: BalanceKey;
  label: string;
  color: string;
}

// Properly type the icons as React components
const BalanceManagement = () => {
  const { addAlert } = useAlert();
  const [users, setUsers] = useState<User[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [balanceData, setBalanceData] = useState({
    balance_type: "ledger" as BalanceKey,
    amount: "",
  });

  // Cast icons to proper React component type
  const BalanceIcon = MdAccountBalance as React.ComponentType<{
    className?: string;
  }>;
  const SearchIcon = MdSearch as React.ComponentType<{ className?: string }>;
  const AddIcon = MdAdd as React.ComponentType<{ className?: string }>;
  const RefreshIcon = MdRefresh as React.ComponentType<{ className?: string }>;

  const balanceTypes: BalanceType[] = [
    { key: "ledger", label: "Ledger Balance", color: "text-blue-600" },
    { key: "stake", label: "Stake Balance", color: "text-green-600" },
    { key: "bonus", label: "Bonus Balance", color: "text-purple-600" },
    { key: "earnings", label: "Earnings Balance", color: "text-orange-600" },
  ];

  // Helper function to safely get balance field name
  const getBalanceField = (key: BalanceKey): BalanceField => {
    return `${key}_balance` as BalanceField;
  };

  // Helper function to get balance value from user
  const getUserBalance = (user: User, key: BalanceKey): number => {
    const field = getBalanceField(key);
    return Number(user[field]) || 0;
  };

  const fetchUsers = async () => {
    try {
      setIsLoading(true);
      const response = await adminAPI.getAllUsers();
      setUsers(response.data);
      setFilteredUsers(response.data);
    } catch (error: any) {
      addAlert({ type: "error", message: "Failed to fetch users" });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const filtered = users.filter(
      (user) =>
        user.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.last_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.username.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredUsers(filtered);
  }, [searchTerm, users]);

  // BalanceManagement.tsx - Update the handleUpdateBalance function
  const handleUpdateBalance = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedUser) return;

    try {
      await adminAPI.updateUserBalance({
        user_id: selectedUser.id,
        balance_type: balanceData.balance_type,
        amount: parseFloat(balanceData.amount),
        // Remove description - backend will generate it automatically
      });

      addAlert({ type: "success", message: "Balance updated successfully" });
      setIsModalOpen(false);
      setBalanceData({ balance_type: "ledger", amount: "" });
      fetchUsers();
    } catch (error: any) {
      addAlert({
        type: "error",
        message: error.response?.data?.error || "Failed to update balance",
      });
    }
  };

  const openBalanceModal = (user: User) => {
    setSelectedUser(user);
    setBalanceData({ balance_type: "ledger", amount: "" });
    setIsModalOpen(true);
  };

  const getTotalBalance = (user: User) => {
    return balanceTypes.reduce((total, balance) => {
      return total + getUserBalance(user, balance.key);
    }, 0);
  };

  const formatBalance = (balance: string | number): string => {
    const num = typeof balance === "string" ? parseFloat(balance) : balance;
    return isNaN(num) ? "0.00" : num.toFixed(2);
  };

  const getTotalPlatformBalance = () => {
    const total = users.reduce((sum, user) => {
      return sum + getTotalBalance(user);
    }, 0);
    return formatBalance(total);
  };

  return (
    <div className={GlobalStyles.container}>
      {/* Header */}
      <div
        className={`${CommonClasses.flexBetween} max-sm:flex-col max-sm:items-start max-sm:gap-4`}
      >
        <div>
          <h1
            className={`${CommonClasses.textXl} font-bold ${CommonClasses.textGrayStrong} mb-2`}
          >
            Balance Management
          </h1>
          <p className={`${CommonClasses.textGray}`}>
            Manage user balances and adjust funds as needed.
          </p>
        </div>
        <button
          onClick={fetchUsers}
          disabled={isLoading}
          className={`${GlobalStyles.buttonIconOutline} ${
            isLoading ? "opacity-50 cursor-not-allowed" : ""
          }`}
        >
          <RefreshIcon
            className={`w-4 h-4 ${isLoading ? "animate-spin" : ""}`}
          />
        </button>
      </div>

      {/* Search and Stats */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        <div className="lg:col-span-3">
          <div className={GlobalStyles.inputWrapper}>
            <SearchIcon className={GlobalStyles.inputIcon} />
            <input
              type="text"
              placeholder="Search users by name, email, or username..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={GlobalStyles.input}
            />
          </div>
        </div>
        <div className={`${GlobalStyles.sectionWrap} text-center`}>
          <div className={`${CommonClasses.textXl} font-bold text-primary`}>
            ${getTotalPlatformBalance()}
          </div>
          <div className={`${CommonClasses.textSm} ${CommonClasses.textGray}`}>
            Total Platform Balance
          </div>
        </div>
      </div>

      {/* Users Table */}
      <div className={GlobalStyles.tableContainer}>
        {isLoading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
            <p className={`${CommonClasses.textGray} mt-4`}>Loading users...</p>
          </div>
        ) : filteredUsers.length === 0 ? (
          <div className="text-center py-8">
            <BalanceIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <p className={`${CommonClasses.textGray} mb-2`}>No users found</p>
            <p className={`${CommonClasses.textSm} ${CommonClasses.textGray}`}>
              {searchTerm
                ? "Try adjusting your search terms"
                : "No users registered yet"}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className={GlobalStyles.table}>
              <thead className={GlobalStyles.tableHeader}>
                <tr>
                  <th className={GlobalStyles.tableHeaderCell}>User</th>
                  {balanceTypes.map((balance) => (
                    <th
                      key={balance.key}
                      className={GlobalStyles.tableHeaderCell}
                    >
                      {balance.label}
                    </th>
                  ))}
                  <th className={GlobalStyles.tableHeaderCell}>
                    Total Balance
                  </th>
                  <th className={GlobalStyles.tableHeaderCell}>Actions</th>
                </tr>
              </thead>
              <tbody className={GlobalStyles.tableBody}>
                {filteredUsers.map((user) => (
                  <tr key={user.id} className={GlobalStyles.tableRow}>
                    <td className={GlobalStyles.tableCell}>
                      <div className={`${CommonClasses.flexStart} gap-3`}>
                        <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center">
                          <span className="text-white font-bold text-sm">
                            {user.first_name[0]}
                            {user.last_name[0]}
                          </span>
                        </div>
                        <div>
                          <div
                            className={`font-medium ${CommonClasses.textGrayStrong}`}
                          >
                            {user.first_name} {user.last_name}
                          </div>
                          <div
                            className={`${CommonClasses.textSm} ${CommonClasses.textGray}`}
                          >
                            @{user.username}
                          </div>
                        </div>
                      </div>
                    </td>
                    {balanceTypes.map((balance) => (
                      <td key={balance.key} className={GlobalStyles.tableCell}>
                        <div className={`font-semibold ${balance.color}`}>
                          ${formatBalance(getUserBalance(user, balance.key))}
                        </div>
                      </td>
                    ))}
                    <td className={GlobalStyles.tableCell}>
                      <div className="font-bold text-primary">
                        ${formatBalance(getTotalBalance(user))}
                      </div>
                    </td>
                    <td className={GlobalStyles.tableCell}>
                      <button
                        onClick={() => openBalanceModal(user)}
                        className={`${GlobalStyles.buttonPrimary} !text-xs`}
                      >
                        <AddIcon className="w-3 h-3 mr-1" />
                        Adjust
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Balance Adjustment Modal */}
      {isModalOpen && selectedUser && (
        <div className={GlobalStyles.modalOverlay}>
          <div className={GlobalStyles.modalContainer}>
            <div
              className={`p-5 border-b border-gray-300 dark:border-gray-700`}
            >
              <h2
                className={`${CommonClasses.textLg} font-semibold ${CommonClasses.textGrayStrong}`}
              >
                Adjust Balance
              </h2>
              <p
                className={`${CommonClasses.textSm} ${CommonClasses.textGray} mt-1`}
              >
                Update balance for {selectedUser.first_name}{" "}
                {selectedUser.last_name}
              </p>
            </div>

            <form onSubmit={handleUpdateBalance} className="p-6 space-y-4">
              {/* Balance Type */}
              <div className={GlobalStyles.formGroup}>
                <label className={GlobalStyles.label}>Balance Type</label>
                <select
                  value={balanceData.balance_type}
                  onChange={(e) =>
                    setBalanceData((prev) => ({
                      ...prev,
                      balance_type: e.target.value as BalanceKey,
                    }))
                  }
                  className={GlobalStyles.input}
                  required
                >
                  {balanceTypes.map((balance) => (
                    <option key={balance.key} value={balance.key}>
                      {balance.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Amount */}
              <div className={GlobalStyles.formGroup}>
                <label className={GlobalStyles.label}>Amount</label>
                <input
                  type="number"
                  step="0.00000001"
                  value={balanceData.amount}
                  onChange={(e) =>
                    setBalanceData((prev) => ({
                      ...prev,
                      amount: e.target.value,
                    }))
                  }
                  placeholder="Enter amount"
                  className={GlobalStyles.input}
                  required
                />
                <p
                  className={`${CommonClasses.textXs} ${CommonClasses.textGray} mt-1`}
                >
                  Positive numbers add funds, negative numbers apply
                  charges/fees
                </p>
              </div>

              {/* Current Balances */}
              <div className={`${GlobalStyles.sectionWrap}`}>
                <h3
                  className={`font-semibold ${CommonClasses.textGrayStrong} mb-2`}
                >
                  Current Balances
                </h3>
                <div className="space-y-1 text-sm">
                  {balanceTypes.map((balance) => (
                    <div
                      key={balance.key}
                      className={`${CommonClasses.flexBetween}`}
                    >
                      <span className={CommonClasses.textGray}>
                        {balance.label}:
                      </span>
                      <span
                        className={`font-medium ${CommonClasses.textGrayStrong}`}
                      >
                        $
                        {formatBalance(
                          getUserBalance(selectedUser, balance.key)
                        )}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-2 pt-4">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className={`${GlobalStyles.buttonOutline} flex-1`}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className={`${GlobalStyles.buttonPrimary} flex-1`}
                >
                  Update Balance
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default BalanceManagement;
